// src/components/session-notes/SessionNotesEditor.js
// TipTap-based editor for per-session notes with auto-save and themes tags.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { saveSessionNotes } from '../../services/session-notes-service';
import '../common/SimpleRichEditor.css';

const DEBOUNCE_MS = 2000;

// ── Menu bar (module scope — avoids remount on every render) ──────────────────
const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const btnStyle = (active, extra = {}) => ({
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    background: active ? '#F3F0FF' : 'transparent',
    color: active ? '#7c3aed' : '#6B7280',
    ...extra,
  });

  return (
    <div style={{
      display: 'flex', gap: '2px', padding: '4px 8px',
      borderBottom: '1px solid #E5E7EB', background: '#FAFAFA',
    }}>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
        style={btnStyle(editor.isActive('bold'), { fontWeight: 'bold' })}
      >B</button>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
        style={btnStyle(editor.isActive('italic'), { fontStyle: 'italic' })}
      >I</button>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}
        style={btnStyle(editor.isActive('bulletList'))}
      >•≡</button>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}
        style={btnStyle(editor.isActive('orderedList'), { fontSize: '12px', fontFamily: 'monospace' })}
      >1.</button>
    </div>
  );
};

// ── SessionNotesEditor ────────────────────────────────────────────────────────
const SessionNotesEditor = ({ bookingId, existingNotes, onSave }) => {
  const [themes, setThemes] = useState(existingNotes?.themes || []);
  const [tagInput, setTagInput] = useState('');
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved'
  const [sharedWithClient, setSharedWithClient] = useState(existingNotes?.sharedWithClient || false);

  const debounceRef = useRef(null);
  // Track latest data for the debounce callback without stale closure issues
  const latestRef = useRef({
    content: existingNotes?.content || '',
    themes: existingNotes?.themes || [],
    sharedWithClient: existingNotes?.sharedWithClient || false,
  });

  const doSave = useCallback(async (content, currentThemes, shared) => {
    setSaveStatus('saving');
    const plainText = content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    try {
      await saveSessionNotes(bookingId, { content, plainText, themes: currentThemes, sharedWithClient: shared ?? latestRef.current.sharedWithClient });
      setSaveStatus('saved');
      onSave?.({ content, plainText, themes: currentThemes });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('SessionNotesEditor: save error:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 5000);
    }
  }, [bookingId, onSave]);

  const scheduleAutoSave = useCallback((content, currentThemes) => {
    latestRef.current = { ...latestRef.current, content, themes: currentThemes };
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => doSave(latestRef.current.content, latestRef.current.themes, latestRef.current.sharedWithClient),
      DEBOUNCE_MS,
    );
  }, [doSave]);

  // Clean up pending debounce on unmount
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: true, listItem: true,
        bold: true, italic: true,
        heading: false, codeBlock: false, blockquote: false,
        horizontalRule: false, code: false, strike: false,
      }),
      Placeholder.configure({ placeholder: 'Write session notes, observations, or next steps...' }),
    ],
    content: existingNotes?.content || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      scheduleAutoSave(html === '<p></p>' ? '' : html, latestRef.current.themes);
    },
  });

  const addTheme = () => {
    const t = tagInput.trim();
    if (!t || themes.includes(t)) { setTagInput(''); return; }
    const next = [...themes, t];
    setThemes(next);
    setTagInput('');
    latestRef.current = { ...latestRef.current, themes: next };
    scheduleAutoSave(latestRef.current.content, next);
  };

  const removeTheme = (t) => {
    const next = themes.filter(x => x !== t);
    setThemes(next);
    latestRef.current = { ...latestRef.current, themes: next };
    scheduleAutoSave(latestRef.current.content, next);
  };

  return (
    <div>
      {/* Save status indicator */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', minHeight: '20px', marginBottom: '4px' }}>
        {saveStatus === 'saving' && (
          <span style={{ fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic' }}>Saving…</span>
        )}
        {saveStatus === 'saved' && (
          <span style={{ fontSize: '12px', color: '#059669', fontWeight: 500 }}>✓ Saved</span>
        )}
        {saveStatus === 'error' && (
          <span style={{ fontSize: '12px', color: '#DC2626', fontStyle: 'italic' }}>Save failed — retrying…</span>
        )}
      </div>

      {/* TipTap editor */}
      <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
        <MenuBar editor={editor} />
        <EditorContent editor={editor} style={{ minHeight: '120px' }} />
      </div>

      {/* Tags */}
      <div style={{ marginTop: '12px' }}>
        <p style={{
          fontSize: '11px', fontWeight: 600, color: '#9CA3AF',
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px',
        }}>
          Tags
        </p>
        <p style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '8px' }}>
          Add keywords to organize your notes (e.g., anxiety, career, grounding)
        </p>

        {themes.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
            {themes.map(t => (
              <span key={t} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '3px 10px', background: '#F3F0FF', color: '#7c3aed',
                borderRadius: '999px', fontSize: '12px', fontWeight: 500,
              }}>
                {t}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); removeTheme(t); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') removeTheme(t); }}
                  style={{ cursor: 'pointer', color: '#7c3aed', fontSize: '14px', lineHeight: 1, userSelect: 'none' }}
                >×</span>
              </span>
            ))}
          </div>
        )}

        <input
          type="text"
          value={tagInput}
          onChange={e => setTagInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTheme(); } }}
          placeholder="Add a tag and press Enter"
          style={{
            width: '100%', padding: '6px 10px', border: '1px solid #E5E7EB',
            borderRadius: '6px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Share with client toggle */}
      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #F3F4F6' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
          <div
            onClick={() => {
              const newVal = !sharedWithClient;
              setSharedWithClient(newVal);
              latestRef.current.sharedWithClient = newVal;
              // Immediate save for toggle change
              const content = latestRef.current.content;
              const plainText = content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
              saveSessionNotes(bookingId, {
                content, plainText, themes: latestRef.current.themes, sharedWithClient: newVal,
              }).then(() => {
                setSaveStatus('saved');
                setTimeout(() => setSaveStatus(null), 3000);
              }).catch(() => {});
            }}
            style={{
              width: '36px', height: '20px', borderRadius: '10px',
              background: sharedWithClient ? '#7c3aed' : '#D1D5DB',
              position: 'relative', transition: 'background 0.2s ease', flexShrink: 0,
            }}
          >
            <div style={{
              width: '16px', height: '16px', borderRadius: '50%', background: '#fff',
              position: 'absolute', top: '2px',
              left: sharedWithClient ? '18px' : '2px',
              transition: 'left 0.2s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            }} />
          </div>
          <div>
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>Share with client</span>
            <p style={{ fontSize: '11px', color: '#9CA3AF', margin: '2px 0 0' }}>
              When enabled, your client can see these notes in their session history
            </p>
          </div>
        </label>
      </div>
    </div>
  );
};

export default SessionNotesEditor;
